@if (inputData$ | async; as inputData) {
  <div class="max-w-xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1">
    <div class="text-center space-y-2 mb-8">
      <h2 class="text-2xl font-bold text-gray-800">Lesson Settings</h2>
      <p class="text-gray-500">Configure duration and technology access for your lesson.</p>
    </div>

    <div class="bg-white p-6 md:p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 space-y-6">
      <!-- Duration Selection -->
      <div>
        <label class="block text-sm font-bold text-gray-700 mb-1">Duration</label>
        <p class="text-xs text-gray-500 mb-2">Choose how long your lesson will be.</p>
        <div class="flex flex-wrap gap-2">
          @for (duration of durations; track duration.value) {
            <button
              type="button"
              (click)="updateDuration(duration.value)"
              class="px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 border cursor-pointer"
              [class.text-white]="inputData.duration === duration.value"
              [class.shadow-md]="inputData.duration === duration.value"
              [class.bg-white]="inputData.duration !== duration.value"
              [class.text-gray-600]="inputData.duration !== duration.value"
              [class.border-gray-200]="inputData.duration !== duration.value"
              [class.hover:border-gray-300]="inputData.duration !== duration.value"
              [class.hover:bg-gray-50]="inputData.duration !== duration.value"
              [style.backgroundColor]="inputData.duration === duration.value ? themeColor : ''"
              [style.borderColor]="inputData.duration === duration.value ? themeColor : ''"
            >
              {{ duration.label }}
            </button>
          }
        </div>
      </div>

      <!-- Instructional Model Selection -->
      <div>
        <label class="block text-sm font-bold text-gray-700 mb-1">Learning Model</label>
        <p class="text-xs text-gray-500 mb-3">Choose an instructional approach for your lesson.</p>
        <div class="grid gap-2">
          @for (model of visibleModels$ | async; track model.id) {
            <div
              class="relative group rounded-xl border transition-all duration-200 cursor-pointer"
              [class.border-brand]="inputData.instructionalModel === model.id"
              [class.bg-brand/5]="inputData.instructionalModel === model.id"
              [class.shadow-md]="inputData.instructionalModel === model.id"
              [class.border-gray-200]="inputData.instructionalModel !== model.id"
              [class.bg-white]="inputData.instructionalModel !== model.id"
              [class.hover:border-gray-300]="inputData.instructionalModel !== model.id"
              [class.hover:bg-gray-50]="inputData.instructionalModel !== model.id"
              [style.borderColor]="inputData.instructionalModel === model.id ? themeColor : ''"
              (click)="updateInstructionalModel(model.id)"
            >
              <div class="flex items-start justify-between p-3">
                <div class="flex-1 min-w-0 pr-2">
                  <div class="flex items-center gap-2 mb-0.5">
                    <span
                      class="font-bold text-sm"
                      [class.text-brand]="inputData.instructionalModel === model.id"
                      [class.text-gray-800]="inputData.instructionalModel !== model.id"
                      [style.color]="inputData.instructionalModel === model.id ? themeColor : ''"
                    >
                      {{ model.name }}
                    </span>
                    <span class="text-xs text-gray-400 font-medium">{{ model.category }}</span>
                  </div>
                  <p class="text-xs text-gray-500">{{ model.shortDescription }}</p>
                </div>
                <button
                  type="button"
                  class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                  (click)="openModelInfo(model, $event)"
                  title="Learn more about {{ model.name }}"
                >
                  <lucide-icon [img]="icons.Info" class="w-4 h-4"></lucide-icon>
                </button>
              </div>
            </div>
          }
          @if (hasHiddenModels) {
            <button
              type="button"
              (click)="toggleShowMoreModels()"
              class="w-full py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center justify-center gap-1 border border-dashed border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50"
            >
              @if (showMoreModels$ | async) {
                <span>Show less</span>
                <lucide-icon [img]="icons.ChevronUp" class="w-4 h-4"></lucide-icon>
              } @else {
                <span>See more models</span>
                <lucide-icon [img]="icons.ChevronDown" class="w-4 h-4"></lucide-icon>
              }
            </button>
          }
        </div>
      </div>

      <!-- Model Info Popover -->
      <app-model-info-popover
        [isOpen]="(isPopoverOpen$ | async) ?? false"
        [model]="(popoverModel$ | async) ?? null"
        (closed)="closeModelInfo()"
      ></app-model-info-popover>

      <!-- Tech Access Selection -->
      <div>
        <label class="block text-sm font-bold text-gray-700 mb-1">Tech Access</label>
        <p class="text-xs text-gray-500 mb-3">What devices do students have access to?</p>
        <div class="grid gap-2">
          @for (option of techRatioOptions; track option.value) {
            <div
              class="relative group rounded-xl border transition-all duration-200 cursor-pointer"
              [class.border-brand]="inputData.techRatio === option.value"
              [class.bg-brand/5]="inputData.techRatio === option.value"
              [class.shadow-md]="inputData.techRatio === option.value"
              [class.border-gray-200]="inputData.techRatio !== option.value"
              [class.bg-white]="inputData.techRatio !== option.value"
              [class.hover:border-gray-300]="inputData.techRatio !== option.value"
              [class.hover:bg-gray-50]="inputData.techRatio !== option.value"
              [style.borderColor]="inputData.techRatio === option.value ? themeColor : ''"
              (click)="updateTechRatio(option.value)"
            >
              <div class="flex items-start justify-between p-3">
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-0.5">
                    <span
                      class="font-bold text-sm"
                      [class.text-brand]="inputData.techRatio === option.value"
                      [class.text-gray-800]="inputData.techRatio !== option.value"
                      [style.color]="inputData.techRatio === option.value ? themeColor : ''"
                    >
                      {{ option.label }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500">{{ option.description }}</p>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Advanced Tech Details (Progressive Disclosure) -->
        @if (inputData.techRatio && inputData.techRatio !== 'none') {
          <div class="mt-3 animate-in">
            <button
              type="button"
              (click)="toggleShowAdvancedTech()"
              class="text-sm font-medium transition-colors cursor-pointer flex items-center gap-1"
              [style.color]="themeColor"
            >
              @if (showAdvancedTech$ | async) {
                <span>− Hide Details</span>
              } @else {
                <span>+ Device Type & Connectivity (Optional)</span>
              }
            </button>

            @if (showAdvancedTech$ | async) {
              <div class="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-4 animate-in">
                <!-- Device Type -->
                <div>
                  <label class="block text-xs font-bold text-gray-600 mb-2">Device Type</label>
                  <div class="flex flex-wrap gap-x-4 gap-y-2">
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.chromebook"
                        (change)="updateTechDetail('chromebook', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      Chromebook
                    </label>
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.ipad"
                        (change)="updateTechDetail('ipad', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      iPad / Tablet
                    </label>
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.laptop"
                        (change)="updateTechDetail('laptop', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      Laptop
                    </label>
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.mobile"
                        (change)="updateTechDetail('mobile', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      Mobile
                    </label>
                  </div>
                </div>

                <!-- Display/Environment -->
                <div>
                  <label class="block text-xs font-bold text-gray-600 mb-2">Environment</label>
                  <div class="flex flex-wrap gap-x-4 gap-y-2">
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.projector"
                        (change)="updateTechDetail('projector', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      Projector
                    </label>
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.smartboard"
                        (change)="updateTechDetail('smartboard', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      Interactive Board
                    </label>
                    <label class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        [checked]="inputData.techDetails.restrictedWifi"
                        (change)="updateTechDetail('restrictedWifi', $any($event.target).checked)"
                        class="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        [style.accentColor]="themeColor"
                      />
                      Blocked YouTube/Streaming
                    </label>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <!-- Error Message -->
      @if (error$ | async; as error) {
        <div class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {{ error }}
        </div>
      }
    </div>

    <!-- Navigation Buttons -->
    <div class="flex gap-3 pt-4 pb-8">
      <button
        (click)="onBack()"
        class="flex-1 py-3 text-gray-500 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
      >
        Back
      </button>
      <button
        (click)="onNext()"
        [disabled]="!inputData.duration || !inputData.instructionalModel || !inputData.techRatio"
        class="flex-[2] py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        [style.backgroundColor]="themeColor"
      >
        Continue
      </button>
    </div>
  </div>
}
